import { useEffect, useRef } from 'react'
import {
  getImportList,
  getImportProgress,
} from '../services/ShipmentBulkUploadServices'
import { ImportStatusId } from '../interfaces/Import/ShipmentBulkUploadApi'
import { alertService } from '../services'

/**
 * Notificación al completar (#4a): pollea en background las importaciones InProcess del
 * usuario y dispara un toast cuando una termina, aunque el operador haya navegado fuera
 * de la vista de detalle. Se monta una vez a nivel de app (App.tsx), gateado por login.
 *
 * Liviano: intervalo de 15s (vs 2s del detalle), pausa cuando la pestaña pierde foco, y
 * en el PRIMER poll solo siembra el set conocido (no notifica importaciones que ya
 * estaban en proceso al abrir la app — evita un flood de toasts viejos).
 */

const POLL_MS = 15000

export function useBulkUploadCompletionNotifier(
  enabled: boolean,
  intervalMs: number = POLL_MS
): void {
  const knownInProcess = useRef<Set<string>>(new Set())
  const seeded = useRef(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    let cancelled = false

    const clearTimer = () => {
      if (timerRef.current != null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }

    const notifyCompleted = async (headerId: string) => {
      const res = await getImportProgress(headerId)
      if (cancelled || !mountedRef.current || res.didError || !res.model) return
      const p = res.model
      if ((p.error_count ?? 0) > 0) {
        alertService.warn(
          `Carga masiva ${p.bulk_upload_number} terminó con errores`,
          `${p.executed_count} de ${p.total_details} guías creadas, ${p.error_count} con error.`
        )
      } else {
        alertService.success(
          `Carga masiva ${p.bulk_upload_number} completada`,
          `${p.executed_count} guías creadas.`
        )
      }
    }

    const tick = async () => {
      if (cancelled || !mountedRef.current) return
      if (
        typeof document !== 'undefined' &&
        document.visibilityState === 'hidden'
      ) {
        schedule()
        return
      }
      const res = await getImportList({
        status_id: ImportStatusId.HeaderInProcess,
        page_size: 100,
      })
      if (cancelled || !mountedRef.current) return
      if (!res.didError && res.model) {
        const currentIds = new Set(
          res.model.results.map((r) => r.shipment_bulk_upload_header_id)
        )
        if (seeded.current) {
          // Las que estaban InProcess y ya no están → terminaron.
          knownInProcess.current.forEach((id) => {
            if (!currentIds.has(id)) notifyCompleted(id)
          })
        } else {
          seeded.current = true
        }
        knownInProcess.current = currentIds
      }
      schedule()
    }

    function schedule() {
      clearTimer()
      timerRef.current = setTimeout(tick, intervalMs)
    }

    const onVisibility = () => {
      // Cancela el timer en espera antes de reanudar: sin esto, el poll agendado y este
      // encadenarían dos getImportList concurrentes.
      if (document.visibilityState === 'visible' && !cancelled) {
        clearTimer()
        tick()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)

    tick()

    return () => {
      cancelled = true
      clearTimer()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [enabled, intervalMs])
}
